import { FC, useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { AddToCollectionModal } from "@/components/modal/add-to-collection/add-to-collection-modal";
import { SearchPageHeader } from "@/pages/search/search-page-header";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import { ShareSheetModal } from "@/components/modal/share-sheet/share-sheet-modal";
import { SearchPageNotesList } from "./components/search-page-notes-list";
import { MDSPanelContainer } from "@/mds";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { SearchPageFloaty } from "@/pages/search/components/search-page-floaty";
import { useTabLocationState } from "@/components/layout/components/tab-bar/use-tab-location-state";
import { useTabContext } from "@/components/layout/tab-context";
import { useAppStore } from "@/store";
import { ImageLightbox } from "@/components/note/media-viewer/image/image-lightbox";
import { PdfLightbox } from "@/components/note/media-viewer/pdf/pdf-lightbox";

interface SearchPageProps {}

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export const SearchPage: FC<SearchPageProps> = observer(() => {
  const { store } = useAppStore();
  const page = useTabPageStore("searchPage");
  page.listState.useListStateEffects();

  const queryString = page.filters.params.queryString;
  const prevQueryString = usePrevious(queryString);
  /**
   * Track views of the search page.
   */
  useEffect(() => {
    if (queryString) {
      trackEvent(TrackedEvent.SearchView, {
        query: queryString,
      });
    }
  }, [queryString]);

  // Retrigger search when navigation sets the retrigger flag, then clear the flag
  const { activeTab } = useTabContext();
  const routerLocationState = useTabLocationState<{ retriggerSearch?: boolean }>();
  useEffect(() => {
    const { retriggerSearch } = routerLocationState || {};
    if (!retriggerSearch) return;

    const queryChanged = prevQueryString !== queryString;

    // Only fire the search from this effect if the query has NOT changed.
    // If it has changed, we rely on the MobX reaction to fire the search.
    if (!queryChanged) {
      page.refreshSearchEngineQuery(page.filters.searchQuery);
    }

    store.navigation.tabs.clearTabState(activeTab.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocationState]);

  return (
    <Wrapper data-test-id="search-page">
      <ShareSheetModal store={page.shareSheetModal} />
      <AddToCollectionModal store={page.addToCollectionModal} />
      <SearchPageHeader />
      <MDSPanelContainer>
        <SearchPageNotesList />
        {page.listState.multiSelectUi}
        <SearchPageFloaty />
      </MDSPanelContainer>
      <PdfLightbox
        isOpen={!!page.pdfLightboxState}
        src={page.pdfLightboxState?.src ?? ""}
        fileName={page.pdfLightboxState?.fileName}
        initialPage={page.pdfLightboxState?.initialPage}
        context="search_result"
        handleClose={page.closePdfLightbox}
        onOpenChange={open => {
          if (!open) {
            page.closePdfLightbox();
          }
        }}
      />
      <ImageLightbox
        isOpen={!!page.imageLightboxState}
        images={page.imageLightboxState?.images}
        initialIndex={page.imageLightboxState?.initialIndex}
        context="search_result"
        handleClose={page.closeImageLightbox}
        onOpenChange={open => {
          if (!open) {
            page.closeImageLightbox();
          }
        }}
      />
    </Wrapper>
  );
});

const Wrapper = styled.div({
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
});
